import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { FUNCOES } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = { nome: "", funcao: "Videomaker", email: "", telefone: "", foto: "", status: "ativo" };

export default function Equipe({ embedded = false }) {
  const [membros, setMembros] = useState([]);
  const [capacidade, setCapacidade] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => {
    api.get("/equipe").then((r) => setMembros(r.data)).catch(() => {});
    api.get("/equipe/capacidade").then((r) => setCapacidade(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const salvar = async (e) => {
    e.preventDefault();
    try {
      await api.post("/equipe", form);
      toast.success("Membro adicionado");
      setDialog(false);
      setForm(empty);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const excluir = async (m) => {
    await api.delete(`/equipe/${m.id}`);
    toast.success("Membro removido");
    load();
  };

  const cap = (id) => capacidade.find((c) => c.membro.id === id);

  return (
    <div className="space-y-6" data-testid="equipe-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="caption-label mb-2">{embedded ? "Configurações // Pessoas e funções" : "Equipe"}</p>
          <h2 className={`${embedded ? "text-2xl sm:text-3xl" : "text-3xl sm:text-4xl"} font-serif text-[#F4F4F0] tracking-tight`}>Equipe do <span className="gold-italic">Estúdio</span></h2>
        </div>
        <Button data-testid="novo-membro-button" onClick={() => setDialog(true)} className="w-full bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] rounded-full font-semibold sm:w-auto">
          <Plus size={16} className="mr-1" /> Novo membro
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {membros.map((m) => {
          const cp = cap(m.id);
          const carga = cp ? Math.min(cp.tarefas_abertas * 10 + cp.atrasadas * 20, 100) : 0;
          return (
            <div key={m.id} data-testid={`membro-card-${m.id}`} className="studio-card studio-card-hover p-5">
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-full bg-[#8CC63F]/12 border border-[#8CC63F]/30 flex items-center justify-center font-serif text-[#8CC63F]">{m.nome[0]}</div>
                <button data-testid={`membro-excluir-${m.id}`} onClick={() => excluir(m)} className="text-[#3F3F46] hover:text-[#F43F5E]"><Trash2 size={14} /></button>
              </div>
              <p className="text-base font-medium text-[#F4F4F0] mt-4">{m.nome}</p>
              <p className="caption-label mt-1 normal-case tracking-normal">{m.funcao}</p>
              {cp && (
                <div className="mt-4 pt-4 border-t border-[#27272A]">
                  <div className="h-1.5 bg-[#27272A] rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${carga}%`, backgroundColor: cp.atrasadas > 0 ? "#F43F5E" : carga > 60 ? "#F59E0B" : "#8CC63F" }} />
                  </div>
                  <div className="flex gap-3 mt-2 font-mono text-[10px] text-[#71717A]">
                    <span>{cp.tarefas_abertas} abertas</span>
                    {cp.em_edicao > 0 && <span>{cp.em_edicao} edição</span>}
                    {cp.atrasadas > 0 && <span className="text-[#F43F5E]">{cp.atrasadas} atrasadas</span>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {membros.length === 0 && <p className="text-sm text-[#71717A]">Nenhum membro cadastrado.</p>}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-[#141418] border-[#27272A] text-[#F4F4F0] max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Novo <span className="gold-italic">membro</span></DialogTitle></DialogHeader>
          <form onSubmit={salvar} className="space-y-4 mt-2">
            <div><Label className="caption-label">Nome</Label>
              <Input data-testid="membro-nome-input" required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
            <div><Label className="caption-label">Função</Label>
              <Select value={form.funcao} onValueChange={(v) => setForm({ ...form, funcao: v })}>
                <SelectTrigger data-testid="membro-funcao-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                  {FUNCOES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="caption-label">E-mail</Label>
              <Input data-testid="membro-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
            <div><Label className="caption-label">Telefone</Label>
              <Input data-testid="membro-telefone-input" value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
            <Button data-testid="membro-submit-button" type="submit" className="w-full bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] rounded-full font-semibold">Adicionar</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
